import { applyClassList, Fragment, jsx } from '@amazon/vinyl-tsx'
import { yieldTask } from '@/util/timeSlice'
import { Header, type HeaderDeps } from '@/header/Header'
import { OfflineToast } from '@/offline/OfflineToast'
import { MobileTopBar } from '@/header/MobileTopBar'
import { getDeviceConfig, isTv } from '@/device/deviceConfig'
import { PhoneLayoutSwitch } from '@/device/phoneLayout'
import { CodeLinkCallout } from '@/auth/PublicCodePrompt'
import { getAuth, notSignedInOrPending, signedIn } from '@/auth/authRef'
import { MapAuth } from '@/auth/MapAuth'
import { nerdStatsVisible } from '@/nerdStats/NerdStatsController'
import { bindThemeColors, bindThemeReset } from '@/components/DynamicThemeColors'
import type {
    CloudQueueController,
    ManagedPlaylistController,
    PlaybackRatePreference,
    Player,
    SleepTimer,
    StreamingSettingsController,
} from '@amzn/bf-playback'
import { AsyncComponentLoader } from '@/components/AsyncComponentLoader'
import { LazyComponent } from '@/components/LazyComponent'
import { mountWhen } from '@/components/mountWhen'
import { trackSelectionUsed$ } from '@/track/trackSelectionStore'
import { FullPlayerOnFirstOpen } from '@/transport/FullPlayerOnFirstOpen'
import { modalVisibleClass$ } from '@/style/modalVisible'
import { AnonymousBanner } from '@/header/AnonymousBanner'
import { ModalLayer } from '@/components/modalLayerHost'
import { onNavigate } from '@/router/router'
import { closeFullScreenOverlaysForNavigation } from '@/util/fullScreenOverlayRegistry'
import { combineData, data } from '@amazon/vinyl-observable'
import { chatInputHidden$ } from '@/router/chatInputHiddenRoutes'
import { chromelessRoute$ } from '@/router/chromelessRoutes'
import { featureEnabled$ } from '@/core/featureGate'
import { FeatureGateEnabled } from '@/components/FeatureGate'
import { createDisposer, type Unsubscribe } from '@amazon/vinyl-util'
import { publishBottomStackHeight } from '@/util/bottomStackHeight'
import type { PartySessionController } from '@/party/PartySessionController'
import { UndoToast } from '@/blackbolt/UndoToast'
import { bindPlaybackState } from '@/blackbolt/playback'
import { getString } from '@/header/i18n/bundle.generated'
import { getAppScroller } from '@/util/domUtil'
import { installLazily } from '@/core/installLazily'
import { ctx } from '@/util/context'
import { bindRightPanelBodyClass } from '@/rightPanel/rightPanelRoute'
import { bindRightPanelWidthVar } from '@/rightPanel/rightPanelWidth'
import { podcastStageShown$ } from '@/nowPlaying/nowPlayingRoute'
import { staticPage } from '@/staticRender'

const { errorHandler } = ctx('AppView')

export type AppViewDeps = HeaderDeps & {
    readonly playerPromise: Promise<Player>
    readonly playlistController: ManagedPlaylistController
    readonly cloudQueueController: CloudQueueController
    readonly sleepTimer: SleepTimer
    readonly streamingSettings: StreamingSettingsController
    readonly playbackRatePreference: PlaybackRatePreference
    readonly partySessionController: PartySessionController
}

export interface AppViewProps {
    readonly deps: AppViewDeps
}

export function AppView(props: AppViewProps) {
    const { authType } = getDeviceConfig()
    const tvAuthPrompt =
        authType === 'map' ? <MapAuthWaiting /> : authType === 'cbl' ? <CodeLinkCallout /> : null
    const headerMenuVisible = data(false)

    return (
        <div
            id="fullscreenRoot"
            className="appView"
            classList={[
                // The podcast stage carries its own transport in the bar's place.
                combineData({
                    current: props.deps.playlistController.current,
                    podcastStage: podcastStageShown$(props.deps.playlistController.current),
                    chromeless: chromelessRoute$,
                }).map(({ current, podcastStage, chromeless }) =>
                    current == null || podcastStage || chromeless ? null : 'showTransport'
                ),
                combineData({ chromeless: chromelessRoute$ }).map(({ chromeless }) =>
                    chromeless ? 'chromeless' : null
                ),
                // Drives `--active-toolbar-height` (styles.scss): reserved toolbar
                // space collapses to 0 on pill-hidden routes (same as BottomToolbar)
                // and whenever the `amp` gate is off (no chat input bar to reserve
                // for). A still-loading gate reserves no space (reveals when enabled).
                combineData({
                    signedIn: signedIn(),
                    hidden: chatInputHidden$,
                    gate: featureEnabled$('amp'),
                }).map(({ signedIn, hidden, gate }) =>
                    signedIn && !hidden && gate.result === true ? 'showToolbar' : null
                ),
            ]}
            onConnect={(root) => {
                const { add, dispose } = createDisposer()
                add(bindThemeColors())
                add(bindThemeReset())

                // Own the `rightPanelOpen` body class (any right-panel surface
                // open) here at the always-mounted app root, so the page's
                // right-rail inset reserves for the queue rail even with the
                // amp chat surface gated off. The width var is owned here too
                // (one publisher, not per-surface) so chat + queue share it.
                add(bindRightPanelBodyClass())
                add(bindRightPanelWidthVar())

                add(
                    installLazily(
                        import('@/fullscreen/fullscreenChromeIdle'),
                        (m) => m.bindFullscreenChromeIdle(root),
                        errorHandler
                    )
                )

                // At app-root, not per-route, so cards on every route (feed, chat)
                // can drive the transport — feed-only binding left `/chat` play no-op.
                bindPlaybackState({
                    cloudQueueController: props.deps.cloudQueueController,
                    playlistController: props.deps.playlistController,
                    playerPromise: props.deps.playerPromise,
                    streamingSettings: props.deps.streamingSettings,
                })
                add(() => bindPlaybackState(null))

                // Transport-visibility body classes drive per-page bottom-inset
                // reservation (playlist save bar, tastes page). Owned at app-root,
                // not the amp-gated chat input bar, so they still track playback
                // when the agent surface is gated off.
                add(
                    props.deps.playlistController.current.onData((entry) => {
                        const hasTransport = entry != null
                        document.body.classList.toggle('hasTransport', hasTransport)
                        document.body.classList.toggle('noTransport', !hasTransport)
                    })
                )
                add(() => document.body.classList.remove('hasTransport', 'noTransport'))

                // Full-screen in-place overlays (mobile FullPlayer, narrow chat
                // pane) aren't routes, so close the still-full-screen ones on each
                // completed nav before they strand over the new page.
                add(onNavigate(closeFullScreenOverlaysForNavigation))
                return dispose
            }}
        >
            <Header id="header" headerMenuVisible={headerMenuVisible} deps={props.deps} />
            {/* App-root sibling, not a header child: as a header descendant it was
                trapped in `#header`'s stacking context (`--z-header`) and painted
                behind the docked chat pane. */}
            <OfflineToast goodNetwork$={props.deps.goodNetwork$} />
            {/* Mobile navigates via a sticky top bar instead of the fixed
                desktop header; both share the HeaderMenu popover below. */}
            <PhoneLayoutSwitch
                render={(phone) =>
                    phone ? <MobileTopBar headerMenuVisible={headerMenuVisible} /> : null
                }
            />
            {/* Avatar dropdown — lazy-loaded off the boot chunk; the shared
                `headerMenuVisible` observable buffers the open toggle until the
                menu module mounts (data() replays its current value). */}
            <LazyComponent
                load={() => import('@/header/HeaderMenu')}
                create={({ HeaderMenu }, p) => <HeaderMenu {...p} />}
                props={{ visible: headerMenuVisible }}
            />

            {/* Persistent polite live region: stays mounted while empty so a
                toast appended into the already-live region announces (gating it
                display:none would drop it from the a11y tree on append). Nested
                role="alert" error toasts override this ancestor to announce
                assertively. */}
            <div id="toastContainer">
                <div className="messages" role="status" aria-live="polite"></div>
            </div>
            {/* At app-root, not just the feed, so mutation-undo confirmations
                (library / like / follow / add-to-playlist) surface on every
                route from one fixed instance. */}
            <UndoToast />
            {!staticPage ? (
                <LazyComponent
                    load={() => import('@/csat/CsatSurveyCard')}
                    create={({ CsatSurveyCard }) => <CsatSurveyCard />}
                    props={{}}
                />
            ) : null}
            {tvAuthPrompt}

            <main id="main-content">
                <div id="stage"></div>
            </main>
            {/* Chat Pane v2 overlay — mounted once at app root, shown/hidden by
                `chatPaneOpen$` (over the page on narrow, a right rail on wide).
                Skipped on a static render (no interactive chat surface) and when
                the `amp` gate is off (the whole agent surface ships dark). */}
            {!staticPage ? (
                <FeatureGateEnabled
                    flag="amp"
                    create={() => (
                        <LazyComponent
                            load={() => import('@/blackbolt/chat/ChatPane')}
                            create={({ ChatPane }) => <ChatPane />}
                            props={{}}
                        />
                    )}
                />
            ) : null}
            {/* Queue panel — the `'queue'` right-panel surface. Mounted once at
                app root, shown by the right-panel route (transport queue button).
                NOT amp-gated: the queue is reachable in the right panel whether or
                not the agent chat surface ships. */}
            {!staticPage ? (
                <LazyComponent
                    load={() => import('@/transport/QueuePanel')}
                    create={({ QueuePanel }, p) => <QueuePanel {...p} />}
                    props={{
                        deps: {
                            playlistController: props.deps.playlistController,
                            cloudQueueController: props.deps.cloudQueueController,
                            partySessionController: props.deps.partySessionController,
                            playerPromise: props.deps.playerPromise,
                        },
                    }}
                />
            ) : null}
            {/* About panel — the `'podcastAbout'` right-panel surface, holding the
                playing podcast episode's description. Mounted beside the queue
                panel so the now-playing stage can open it from either the audio or
                the video presentation. */}
            {!staticPage ? (
                <LazyComponent
                    load={() =>
                        Promise.all([
                            import('@/podcast/PodcastAboutPanel'),
                            import('@/transport/TvNanoPlayer'),
                        ])
                    }
                    create={([{ PodcastAboutPanel }, { TvNanoPlayer }], p) => (
                        <PodcastAboutPanel
                            deps={{
                                ...p.deps,
                                dock: isTv() ? (
                                    <TvNanoPlayer
                                        placement="panel"
                                        playlistController={p.deps.playlistController}
                                    />
                                ) : undefined,
                            }}
                        />
                    )}
                    props={{
                        deps: { playlistController: props.deps.playlistController },
                    }}
                />
            ) : null}
            {/* Bottom-stack column — ONE fixed flex-column stacking the floating
                bottom pills (mini player, then chat input toolbar) as flow
                children, so hiding any one reflows the rest with no per-element
                `bottom:` math. The solid desktop transport
                (`#footer`) stays a separate context below — it owns the FullPlayer
                takeover z-index promotion. Skipped on a static render. */}
            {!staticPage && (
                <div
                    id="bottomStack"
                    // Publishes live height as `--bottom-stack-height` so consumers
                    // reserve one measured clearance; a hidden child collapses out
                    // of it automatically. See `util/bottomStackHeight`.
                    onConnect={(stack) => publishBottomStackHeight(stack)}
                >
                    {/* Narrow-width mini player — flows above the chat pill; its
                        own loader shares the cached Transport chunk. Hidden on
                        desktop / when nothing plays, so the column shrinks. */}
                    <AsyncComponentLoader
                        createComponent={({ MiniPlayer, player }) => (
                            <MiniPlayer
                                deps={{
                                    player,
                                    playlistController: props.deps.playlistController,
                                    cloudQueueController: props.deps.cloudQueueController,
                                    sleepTimer: props.deps.sleepTimer,
                                    streamingSettings: props.deps.streamingSettings,
                                    playbackRatePreference: props.deps.playbackRatePreference,
                                }}
                            />
                        )}
                        loadData={async () => {
                            // Off the view's build task, as LazyComponent starts its import.
                            await yieldTask()
                            const [player, { MiniPlayer }] = await Promise.all([
                                props.deps.playerPromise,
                                import('@/transport/Transport'),
                            ])
                            return { player, MiniPlayer }
                        }}
                    />
                    {/* Persistent chat input toolbar — tracks the route
                        internally and stays live across navigation. Gated off with
                        the rest of the agent surface when the `amp` gate is off. */}
                    <FeatureGateEnabled
                        flag="amp"
                        create={() => (
                            <LazyComponent
                                load={() => import('@/blackbolt/BottomToolbar')}
                                create={({ BottomToolbar }) => <BottomToolbar />}
                                props={{}}
                            />
                        )}
                    />
                </div>
            )}

            {/* Bulk-actions toolbar — at app-root so it stays live across every
                route the app-wide track-selection store spans (playlist / album
                detail, library songs). */}
            {mountWhen(trackSelectionUsed$, () => (
                <LazyComponent
                    load={() => import('@/bulkActions/BulkActionsToolbar')}
                    create={({ BulkActionsToolbar }, p) => <BulkActionsToolbar {...p} />}
                    props={{
                        deps: {
                            playerPromise: props.deps.playerPromise,
                            playlistController: props.deps.playlistController,
                            cloudQueueController: props.deps.cloudQueueController,
                        },
                    }}
                />
            ))}

            {/* App-level modal stack, centralized here so virtualized rows
                can't orphan portalled modals inside `<body>`. */}
            <ModalLayer />

            <div id="footer">
                <AnonymousBanner />
                {/* Transport + NerdStats are dynamic imports resolved alongside
                    the player promise — transport-surface UI that doesn't belong
                    in the app-shell chunk. */}
                <AsyncComponentLoader
                    createComponent={({ player, NerdStats, DeviceTransport }) => (
                        <>
                            <NerdStats
                                visible={nerdStatsVisible}
                                deps={{
                                    quality: player.amznPlaybackQuality,
                                    playlistController: props.deps.playlistController,
                                }}
                            />
                            <DeviceTransport
                                deps={{
                                    player,
                                    playlistController: props.deps.playlistController,
                                    cloudQueueController: props.deps.cloudQueueController,
                                    sleepTimer: props.deps.sleepTimer,
                                    streamingSettings: props.deps.streamingSettings,
                                    playbackRatePreference: props.deps.playbackRatePreference,
                                }}
                            />
                            {/* Mobile expanded player — an in-place overlay (not a route), so
                                playback + back-stack are untouched. Stays in `#footer` to reuse
                                its z-index promotion; the mini-player it opens from lives in
                                `#bottomStack`. */}
                            <FullPlayerOnFirstOpen
                                deps={{
                                    player,
                                    playerPromise: props.deps.playerPromise,
                                    playlistController: props.deps.playlistController,
                                    cloudQueueController: props.deps.cloudQueueController,
                                    sleepTimer: props.deps.sleepTimer,
                                    streamingSettings: props.deps.streamingSettings,
                                    playbackRatePreference: props.deps.playbackRatePreference,
                                    partySessionController: props.deps.partySessionController,
                                }}
                            />
                        </>
                    )}
                    loadData={async () => {
                        await yieldTask()
                        const [player, { NerdStats }, { DeviceTransport }] = await Promise.all([
                            props.deps.playerPromise,
                            import('@/nerdStats/NerdStats'),
                            import('@/transport/Transport'),
                        ])
                        return { player, NerdStats, DeviceTransport }
                    }}
                />
            </div>
        </div>
    )
}

/** Builds the app view, returning the function that mounts it. */
export function createAppView(props: AppViewProps): () => Unsubscribe {
    const appView = <AppView {...props} />
    return () => mountAppView(appView)
}

function mountAppView(appView: Element): Unsubscribe {
    document.body.append(appView)

    // Lock scrolling on the page scroller (`#main-content`, the element that
    // scrolls now that the document doesn't) while a modal is visible.
    applyClassList(getAppScroller() as HTMLElement, [modalVisibleClass$])
    return () => appView.remove()
}

function MapAuthWaiting() {
    // MAP resolves PENDING -> SIGNED_IN and never reports SIGNED_OUT, so this
    // waiting message must show for the whole not-yet-signed-in window (PENDING
    // included) — gating on the SIGNED_OUT-only `notSignedIn` would hide it.
    // Once the shell reports MAP vended no token, it says so rather than wait on one that won't come.
    const auth = getAuth()
    const failure = auth instanceof MapAuth ? auth.failure : data<string | null>(null)
    return (
        <div className="mapAuthWaiting" visible={notSignedInOrPending()}>
            <span visible={failure.map((reason) => reason === null)}>
                {getString('dmusic_map_auth_waiting_text')}
            </span>
            <span visible={failure.map((reason) => reason !== null)}>
                {getString('dmusic_map_auth_failed_text')}
            </span>
        </div>
    )
}
